import React from "react";
import { Autocomplete, TextField } from "@mui/material";
import { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";

// Define props interface with generic type T for options
interface AutoCompleteHelperProps<T extends Record<string, any>> {
    name: string;
    label: string;
    optionsArray: T[];
    value?: T | null | T[];
    setValue?: (e: any) => void;
    multiple?: boolean;
}

function AutoCompleteHelper<T extends Record<string, any>>({
    name,
    label,
    optionsArray,
    value,
    setValue,
    multiple = false,
}: AutoCompleteHelperProps<T>) {

    // Directly use props, remove internal state management for options/render
    const handleChange = (_event: React.SyntheticEvent, newValue: T | T[] | null) => {
        if (setValue) setValue(newValue);
    }

    // Ensure optionsArray is always an array before passing to Autocomplete
    const currentOptions = Array.isArray(optionsArray) ? optionsArray : [];

    // Determine the value to pass based on multiple flag, defaulting appropriately
    const currentValue = multiple ? (value || []) : (value || null);

    return (
        <Autocomplete
            id={`autocomplete-${name}`}
            // Pass the validated options array directly
            options={currentOptions}
            // Pass the determined value
            value={currentValue}
            multiple={multiple}
            onChange={handleChange}
            getOptionLabel={(option: T) => {
                // Simplified check using optional chaining and nullish coalescing
                return String(option?.[name] ?? '');
            }}
            isOptionEqualToValue={(option, val) => {
                // Ensure both option and value are valid objects with an 'id' before comparing
                // This prevents errors if either is null/undefined or lacks the id property
                return !!option && !!val && option.id === val.id;
            }}
            renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                    {...params}
                    name={name}
                    label={label}
                    variant="outlined"
                    fullWidth
                />
            )}
        />
    );
}

export default AutoCompleteHelper;